import { Component, Inject, OnInit, TemplateRef, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AgGridAngular } from 'ag-grid-angular';
import { ToastrService } from 'ngx-toastr';
import { BaseApi } from 'src/backend/api/base-api';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { TranslaterService } from 'src/app/services/translater.service';
import { UserConfigurationsService } from 'src/app/services/user-configurations.service';
import { prevTaskService } from 'src/app/services/prev-task.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ChangeDetectorRef } from '@angular/core';
import { ActionEditDeleteBtnRendererTechnicianSubTask } from 'src/app/action-edit-delete-btn-renderer-technician-sub-task.component';
import { Constants } from 'src/@smartmanufacturing/common/constants';
import Swal from 'sweetalert2';

@Component({
    selector: 'app-wo-subtasks-dialog',
    templateUrl: './wo-subtasks-dialog.component.html',
    styleUrls: ['./wo-subtasks-dialog.component.scss']
})
export class WOSubtasksDialogComponent implements OnInit {
    @ViewChild('agGrid') agGrid!: AgGridAngular;
    @ViewChild('editSubTaskDialog') private editSubTaskDialog!: TemplateRef<any>;
    @ViewChild('fileInput') fileInput!: ElementRef;

    subTaskForm: FormGroup;
    userSession: any;
    rowData: any[] = [];
    languageDir = 'ltr';
    langObj: any = {};
    moduleId!: any;
    pageId!: any;
    gridApi: any;
    gridColumnApi: any;
    isGridReady: boolean = false;
    isDataReady: boolean = false;
    spareParts: any = [
        { ID: 1, Name: 'Yes' },
        { ID: 0, Name: 'No' },
    ];
    isSparePartsYes: boolean = false;
    selectedSubTask: any = null;
    technicianList: any[] = [];
    vendorsList: any[] = [];
    groupCode: any;
    plantCode: any;
    userCode: any;
    responsibleName: string = '';
    externalVendorName: string = '';
    editDialogRef: MatDialogRef<any> | null = null;
    blobStorageUrl = Constants.BLOB_STORAGE_URL;
    selectedFile: File | null = null;
    filePreview: any = null;
    maxFileSize = 5 * 1024 * 1024; // 5MB in bytes
    isFromManager: boolean = false;
    isViewMode: boolean = false;
    hideActionColumn: boolean = false;
    viewOnlyMode: boolean = false;
    approvalDateTime: Date | null = null;

    columnDefs: any[] = [];

    defaultColDef = {
        flex: 1,
        sortable: true,
        resizable: true,
        wrapText: true,
        filter: 'agSetColumnFilter',
    };

    frameworkComponents: any = {
        ActionEditDeleteBtnRendererTechnicianSubTask: ActionEditDeleteBtnRendererTechnicianSubTask,
    };

    constructor(
        private translater: TranslaterService,
        private translate: TranslateService,
        private baseApi: BaseApi,
        private prevTaskService: prevTaskService,
        private toaster: ToastrService,
        private fb: FormBuilder,
        private spinner: NgxSpinnerService,
        private cdr: ChangeDetectorRef,
        private dialog: MatDialog,
        public dialogRef: MatDialogRef<WOSubtasksDialogComponent>,
        private userConfigService: UserConfigurationsService,
        @Inject(MAT_DIALOG_DATA) public data: any
    ) {
        this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
            this.languageDir = event.lang === 'ar' ? 'rtl' : 'ltr';
            this.getLangWords();
        });

        this.subTaskForm = this.fb.group({
            Id: [],
            woDetails: [{ value: '', disabled: true }],
            targetDate: [{ value: '', disabled: true }],
            subTaskIsMachineBreakdown: [''],
            subTaskIsSpareParts: [''],
            subTaskSparePartCode: [''],
            subTaskStartTime: [{ value: '', disabled: true }],
            subTaskSPRequestTime: [''],
            subTaskSPReceivedTime: [''],
            subTaskCompleteTime: [''],
            responsible: [''],
            externalVendor: [''],
            remarks: [{ value: '', disabled: true }],
        });
    }

    ngOnInit(): void {
        this.getLangWords();
        this.userSession = this.baseApi.getUserSession();
        const plantSession = this.baseApi.getPlantFromStorage();
        const groupSession = this.baseApi.getGroupFromStorage();

        this.plantCode = plantSession?.plantCode;
        this.groupCode = groupSession?.groupCode;
        this.userCode = this.userSession?.userCode;
        this.isFromManager = this.data?.isFromManager || false;
        this.hideActionColumn = this.data?.hideActionColumn || false;
        this.viewOnlyMode = this.data?.viewOnlyMode || false;

        if (this.data?.rowData) {
            // Map the row data to ensure all fields are properly formatted
            this.rowData = this.data.rowData.map((item: any) => ({
                id: item.Id || item.id,
                woDetails: item.Detail || item.woDetails,
                responsible: item.ResponsibleName,
                responsibleId: item.ResponsibleID || item.responsibleId,
                externalVendor: item.ExternalVendor || item.externalVendor || null,
                externalVendorId: item.ExternalVendorId || item.externalVendorId,
                status: item.Status || item.status,
                targetDate: item.TargetDate || item.targetDate,
                completionDate: item.CompletionDate || item.completionDate,
                CreatedBy: item.CreatedByName,
                UpdatedBy: item.UpdatedByName,
                subTaskIsMachineBreakdown: item.SubTaskIsMachineBreakdown !== undefined ? item.SubTaskIsMachineBreakdown : item.subTaskIsMachineBreakdown,
                subTaskIsSpareParts: item.SubTaskIsSpareParts !== undefined ? item.SubTaskIsSpareParts : item.subTaskIsSpareParts,
                subTaskSparePartCode: item.SubTaskSparePartCode || item.subTaskSparePartCode || '',
                subTaskStartTime: item.SubTaskStartTime || item.subTaskStartTime || null,
                subTaskSPRequestTime: item.SubTaskSPRequestTime || item.subTaskSPRequestTime || null,
                subTaskSPReceivedTime: item.SubTaskSPReceivedTime || item.subTaskSPReceivedTime || null,
                remarks: item.SubTaskRemarks || item.subTaskRemarks || item.Remarks || null,
                attachmentPath: item.AttachmentPath || item.attachmentPath || null,
                attachmentType: item.AttachmentType || item.attachmentType || null,
                attachmentExtension: item.AttachmentExtension || item.attachmentExtension || null,
                isApproved: item.IsApproved !== undefined && item.IsApproved !== null ? item.IsApproved : null,
                approvalDate: item.ApprovalDate || item.approvalDate || null,
                approvedBy: item.ApprovedBy || item.approvedBy || null,
                WaitingSPTime: item.WaitingSPTimeMinutes || item.waitingSPTimeMinutes || null,
                TroubleShootTime: item.TroubleShootTimeMinutes || item.troubleShootTimeMinutes || null,
                ResponseTime: item.ResponseTimeMinutes || item.responseTimeMinutes || null,
                RepairTime: item.RepairTimeMinutes || item.repairTimeMinutes || null,
            }));
            this.isDataReady = true;
        }

        if (this.data?.technicianList) {
            this.technicianList = this.data.technicianList;
        }

        if (this.data?.vendorsList) {
            this.vendorsList = this.data.vendorsList;
        }

        this.setupColumns();
    }

    setupColumns(): void {
        this.columnDefs = [];

        // Add Action column only if not hidden
        if (!this.hideActionColumn) {
            this.columnDefs.push({
                headerName: 'Action',
                field: 'action',
                cellRenderer: 'ActionEditDeleteBtnRendererTechnicianSubTask',
                headerValueGetter: this.localizeHeader.bind(this),
                cellRendererParams: {
                    onClickEdit: this.onEditClickFromGrid.bind(this),
                    onClickView: this.onViewClickFromGrid.bind(this),
                    isUpdate: true,
                    isDelete: false,
                    canEdit: (params: any) => {
                        // Enable edit only if ResponsibleID matches logged-in user
                        return params.data.responsibleId === this.userCode;
                    }
                },
                pinned: 'left',
                width: 100,
                minWidth: 100,
                sortable: false,
                filter: false,
            },
                {
                    headerName: 'Approval',
                    field: 'approval',
                    headerValueGetter: this.localizeHeader.bind(this),
                    minWidth: 100,
                    cellRenderer: (params: any) => {
                        const isApproved = params.data.isApproved;
                        const approvalDate = params.data.approvalDate;
                        const approvedBy = params.data.approvedBy;

                        // Check if not yet reviewed (ApprovalDate and ApprovedBy are null)
                        const isNotReviewed = !approvalDate && !approvedBy;

                        // If not yet reviewed and manager view - show button
                        if (this.isFromManager && isNotReviewed && !this.viewOnlyMode) {
                            return '<button class="btn btn-sm btn-success" style="padding: 6px 16px;color:white; border-radius: 4px; font-weight: 500; transition: all 0.2s; background-color: #28a745; border-color: #28a745;"><i class="fa fa-check" style="margin-right: 5px;"></i>Approve</button>';
                        }

                        // If not from manager (technician view) and not yet reviewed - show pending
                        if (!this.isFromManager && isNotReviewed) {
                            return '<span style="color: #ffc107; font-weight: bold; font-size: 13px;"><i class="fa fa-clock-o" style="margin-right: 4px;"></i>Approval Pending</span>';
                        }

                        // If approved (has approval date and approved by)
                        if (!isNotReviewed && (isApproved === true || isApproved === 1)) {
                            return '<span style="color: #28a745; font-weight: bold; font-size: 13px;"><i class="fa fa-check-circle" style="margin-right: 4px;"></i>Approved</span>';
                        }

                        // If disapproved (has approval date and approved by, but isApproved is false)
                        if (!isNotReviewed && (isApproved === false || isApproved === 0)) {
                            return '<span style="color: #dc3545; font-weight: bold; font-size: 13px;"><i class="fa fa-times-circle" style="margin-right: 4px;"></i>Disapproved</span>';
                        }

                        return '';
                    },
                    onCellClicked: (params: any) => {
                        const approvalDate = params.data.approvalDate;
                        const approvedBy = params.data.approvedBy;
                        const isNotReviewed = !approvalDate && !approvedBy;

                        if (this.isFromManager && isNotReviewed && !this.viewOnlyMode) {
                            this.openApprovalConfirmation(params.data);
                        }
                    },
                    pinned: 'left',
                    width: 160,
                    sortable: false,
                    filter: false,
                },
            );
        }

        // Add remaining columns
        this.columnDefs.push(
            {
                headerName: 'WO Details',
                field: 'woDetails',
                headerValueGetter: this.localizeHeader.bind(this),
                minWidth: 100,
            },
            {
                headerName: 'Responsible',
                field: 'responsible',
                width: 150,
                minWidth: 100,
                headerValueGetter: this.localizeHeader.bind(this),
            },
            {
                headerName: 'External Vendor',
                field: 'externalVendor',
                headerValueGetter: this.localizeHeader.bind(this),
                minWidth: 100,
            },
            {
                headerName: 'Status',
                field: 'status',
                minWidth: 100,
                cellStyle: (params: any) => {
                    if (params.value === 'Pending') {
                        return {
                            backgroundColor: '#FFFF00',
                            color: 'black',
                            fontWeight: 'bold',
                        };
                    } else if (params.value === 'InProcess') {
                        return {
                            backgroundColor: '#FFA500',
                            color: 'white',
                            fontWeight: 'bold',
                        };
                    }
                    else if (params.value === 'Disapproved') {
                        return {
                            color: 'red',
                            fontWeight: 'bold',
                        };
                    }
                    else if (params.value === 'Closed' || params.value === 'Completed') {
                        return {
                            backgroundColor: '#38CF36',
                            color: 'white',
                            fontWeight: 'bold',
                        };
                    }
                    return null;
                },
                headerValueGetter: this.localizeHeader.bind(this),
            },
            {
                headerName: 'Target Date',
                field: 'targetDate',
                valueFormatter: (params: any) =>
                    params.value ? this.formatCellData(params.value, 'dateOnly') : null,
                headerValueGetter: this.localizeHeader.bind(this),
                minWidth: 100,
            },
            {
                headerName: 'Completion Date',
                field: 'completionDate',
                valueFormatter: (params: any) =>
                    params.value ? this.formatCellData(params.value, 'dateTime') : null,
                headerValueGetter: this.localizeHeader.bind(this),
                minWidth: 100,
            },
            {
                headerName: 'CreatedBy',
                field: 'CreatedBy',
                headerValueGetter: this.localizeHeader.bind(this),
                minWidth: 100,
            },
            {
                headerName: 'UpdatedBy',
                field: 'UpdatedBy',
                headerValueGetter: this.localizeHeader.bind(this),
                minWidth: 100,
            },
            {
                headerName: 'Machine Breakdown',
                field: 'subTaskIsMachineBreakdown',
                valueFormatter: (params: any) => {
                    if (params.value === true || params.value === 1) return 'Yes';
                    if (params.value === false || params.value === 0) return 'No';
                    return '';
                },
                headerValueGetter: this.localizeHeader.bind(this),
                minWidth: 100,
            },
            {
                headerName: 'Spare Parts',
                field: 'subTaskIsSpareParts',
                valueFormatter: (params: any) => {
                    if (params.value === true || params.value === 1) return 'Yes';
                    if (params.value === false || params.value === 0) return 'No';
                    return '';
                },
                headerValueGetter: this.localizeHeader.bind(this),
                minWidth: 200,
            },
            {
                headerName: 'Spare Part Code',
                field: 'subTaskSparePartCode',
                headerValueGetter: this.localizeHeader.bind(this),
                minWidth: 200,
            },
            {
                headerName: 'Start Time',
                field: 'subTaskStartTime',
                valueFormatter: (params: any) =>
                    params.value ? this.formatCellData(params.value, 'dateTime') : null,
                headerValueGetter: this.localizeHeader.bind(this),
                minWidth: 200,
            },
            {
                headerName: 'SP Request Time',
                field: 'subTaskSPRequestTime',
                valueFormatter: (params: any) =>
                    params.value ? this.formatCellData(params.value, 'dateTime') : null,
                headerValueGetter: this.localizeHeader.bind(this),
                minWidth: 200,
            },
            {
                headerName: 'SP Received Time',
                field: 'subTaskSPReceivedTime',
                valueFormatter: (params: any) =>
                    params.value ? this.formatCellData(params.value, 'dateTime') : null,
                headerValueGetter: this.localizeHeader.bind(this),
                minWidth: 200,
            },
            {
                headerName: 'Remarks',
                field: 'remarks',
                headerValueGetter: this.localizeHeader.bind(this),
                minWidth: 200,
            },
            {
                headerName: 'Attachment',
                field: 'attachmentPath',
                width: 120,
                minWidth: 200,
                cellRenderer: (params: any) => {
                    if (params.value) {
                        const fullUrl = this.blobStorageUrl + params.value;
                        const extension = params.data.attachmentExtension?.toLowerCase() || '';
                        const isImage = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'tiff', 'webp'].includes(extension);

                        if (isImage) {
                            return `<a href="${fullUrl}" target="_blank" title="View Image"><i class="fa fa-image" style="font-size:18px; color:#007bff;"></i></a>`;
                        } else {
                            return `<a href="${fullUrl}" download target="_blank" title="Download File"><i class="fa fa-download" style="font-size:18px; color:#007bff;"></i></a>`;
                        }
                    }
                    return '';
                },
                headerValueGetter: this.localizeHeader.bind(this),
            }
        );
        if (this.hideActionColumn) {
            this.columnDefs.push(
                {
                    headerName: 'Repair Time',
                    field: 'RepairTime',
                    headerValueGetter: this.localizeHeader.bind(this),
                    minWidth: 200,
                    cellRenderer: function (params: any) {

                        if (!params || params.value == null)
                            return '';

                        let totalSeconds = Number(params.value);

                        let minutes = Math.floor(totalSeconds / 60);
                        let seconds = totalSeconds % 60;

                        return minutes + ' min ' + seconds + ' sec';
                    }
                },
                {
                    headerName: 'Response Time',
                    field: 'ResponseTime',
                    headerValueGetter: this.localizeHeader.bind(this),
                    minWidth: 200,
                    cellRenderer: function (params: any) {

                        if (!params || params.value == null)
                            return '';

                        let totalSeconds = Number(params.value);

                        let minutes = Math.floor(totalSeconds / 60);
                        let seconds = totalSeconds % 60;

                        return minutes + ' min ' + seconds + ' sec';
                    }
                },
                {
                    headerName: 'TroubleShoot Time',
                    field: 'TroubleShootTime',
                    headerValueGetter: this.localizeHeader.bind(this),
                    minWidth: 200,
                    cellRenderer: function (params: any) {

                        if (!params || params.value == null)
                            return '';

                        let totalSeconds = Number(params.value);

                        let minutes = Math.floor(totalSeconds / 60);
                        let seconds = totalSeconds % 60;

                        return minutes + ' min ' + seconds + ' sec';
                    }
                },
                {
                    headerName: 'WaitingSP Time',
                    field: 'WaitingSPTime',
                    headerValueGetter: this.localizeHeader.bind(this),
                    minWidth: 200,
                    cellRenderer: function (params: any) {

                        if (!params || params.value == null)
                            return '';

                        let totalSeconds = Number(params.value);

                        let minutes = Math.floor(totalSeconds / 60);
                        let seconds = totalSeconds % 60;

                        return minutes + ' min ' + seconds + ' sec';
                    }
                },
            );
        }
    }

    onGridReady(params: any): void {
        this.gridApi = params.api;
        this.gridColumnApi = params.columnApi;
        this.isGridReady = true;
        this.setGridDataIfReady();
    }

    setGridDataIfReady(): void {
        if (this.isGridReady && this.isDataReady) {
            this.gridApi.setColumnDefs(this.columnDefs);
            this.gridApi.setRowData(this.rowData);
            this.gridColumnApi.autoSizeAllColumns();
        }
    }

    onEditClickFromGrid(params: any): void {
        this.isViewMode = false;
        this.onEditClick(params.rowData);
    }

    onViewClickFromGrid(params: any): void {
        this.isViewMode = true;
        this.onEditClick(params.rowData);
    }

    onEditClick(data: any): void {
        this.selectedSubTask = data;

        // Store approval date for validation
        this.approvalDateTime = data.approvalDate ? new Date(data.approvalDate) : null;

        // Set display names for disabled fields
        this.responsibleName = data.responsible || '';
        this.externalVendorName = data.externalVendor || '';

        // Load existing attachment if available
        if (data.attachmentPath) {
            const fullUrl = this.blobStorageUrl + data.attachmentPath;
            const extension = data.attachmentExtension?.toLowerCase() || '';
            const fileName = data.attachmentPath.split('/').pop() || 'file';
            const fileType = this.getFileTypeFromExtension(extension);

            this.filePreview = {
                FileName: fileName,
                FileExtension: extension,
                ImageSrc: fullUrl,
                FileType: fileType
            };
        } else {
            this.filePreview = null;
            this.selectedFile = null;
        }

        // Populate form with data
        this.subTaskForm.patchValue({
            Id: data.id,
            woDetails: data.woDetails,
            targetDate: data.targetDate ? new Date(data.targetDate) : null,
            subTaskIsMachineBreakdown: data.subTaskIsMachineBreakdown === true ? 1 : data.subTaskIsMachineBreakdown === false ? 0 : '',
            subTaskIsSpareParts: data.subTaskIsSpareParts === true ? 1 : data.subTaskIsSpareParts === false ? 0 : '',
            subTaskSparePartCode: data.subTaskSparePartCode || '',
            subTaskStartTime: data.subTaskStartTime ? new Date(data.subTaskStartTime) : null,
            subTaskSPRequestTime: data.subTaskSPRequestTime ? new Date(data.subTaskSPRequestTime) : null,
            subTaskSPReceivedTime: data.subTaskSPReceivedTime ? new Date(data.subTaskSPReceivedTime) : null,
            subTaskCompleteTime: data.completionDate ? new Date(data.completionDate) : null,
            responsible: data.responsibleId,
            externalVendor: data.externalVendorId,
            remarks: data.remarks || '',
        });

        // Conditionally disable/enable subTaskStartTime based on whether it has a value
        if (data.subTaskStartTime) {
            this.subTaskForm.controls['subTaskStartTime'].disable();
        } else {
            this.subTaskForm.controls['subTaskStartTime'].enable();
        }

        // If in view mode, disable all form controls
        if (this.isViewMode) {
            Object.keys(this.subTaskForm.controls).forEach(key => {
                this.subTaskForm.controls[key].disable();
            });
        }

        // Set validation for spare parts
        this.setValidateSubTaskSparePartsCode();

        // Setup time validation listeners
        this.setupTimeValidationListeners();

        // Open dialog and store reference
        this.editDialogRef = this.dialog.open(this.editSubTaskDialog, {
            height: 'auto',
            width: '90%',
            disableClose: false,
        });

        this.editDialogRef.afterClosed().subscribe(() => {
            this.subTaskForm.reset();
            this.selectedSubTask = null;
            this.editDialogRef = null;
            this.isViewMode = false;
            this.cdr.detectChanges();
        });
    }

    setValidateSubTaskSparePartsCode(): void {
        this.isSparePartsYes = this.subTaskForm.get('subTaskIsSpareParts')?.value === 1;

        if (this.isSparePartsYes) {
            // Enable fields and set validators
            this.subTaskForm.controls['subTaskSparePartCode'].enable();
            this.subTaskForm.controls['subTaskSPRequestTime'].enable();
            this.subTaskForm.controls['subTaskSPReceivedTime'].enable();

            this.subTaskForm.controls['subTaskSparePartCode'].setValidators([Validators.required]);
            this.subTaskForm.controls['subTaskSPRequestTime'].setValidators([Validators.required]);
        } else {
            // Check if fields have values
            const hasSparePartCode = this.subTaskForm.get('subTaskSparePartCode')?.value;
            const hasSPRequestTime = this.subTaskForm.get('subTaskSPRequestTime')?.value;
            const hasSPReceivedTime = this.subTaskForm.get('subTaskSPReceivedTime')?.value;

            // Disable fields only if they don't have values
            if (!hasSparePartCode) {
                this.subTaskForm.controls['subTaskSparePartCode'].disable();
                this.subTaskForm.controls['subTaskSparePartCode'].setValue('');
            }
            if (!hasSPRequestTime) {
                this.subTaskForm.controls['subTaskSPRequestTime'].disable();
                this.subTaskForm.controls['subTaskSPRequestTime'].setValue('');
            }
            if (!hasSPReceivedTime) {
                this.subTaskForm.controls['subTaskSPReceivedTime'].disable();
                this.subTaskForm.controls['subTaskSPReceivedTime'].setValue('');
            }

            // Clear validators
            this.subTaskForm.controls['subTaskSparePartCode'].clearValidators();
            this.subTaskForm.controls['subTaskSPRequestTime'].clearValidators();
        }

        this.subTaskForm.controls['subTaskSparePartCode'].updateValueAndValidity();
        this.subTaskForm.controls['subTaskSPRequestTime'].updateValueAndValidity();
        this.subTaskForm.controls['subTaskSPReceivedTime'].updateValueAndValidity();
    }

    validateTimeConstraints(): boolean {
        const formValue = this.subTaskForm.getRawValue();
        const startTime = formValue.subTaskStartTime;
        const completeTime = formValue.subTaskCompleteTime;
        const spRequestTime = formValue.subTaskSPRequestTime;
        const spReceivedTime = formValue.subTaskSPReceivedTime;
        const currentTime = new Date();

        // Start time must be greater than approval date if approval date exists
        if (startTime && this.approvalDateTime) {
            const startDate = new Date(startTime);
            if (startDate <= this.approvalDateTime) {
                this.toaster.error('Start Time must be greater than Approval Date Time');
                return false;
            }
        }

        // If complete time is set, start time must be set
        if (completeTime && !startTime) {
            this.toaster.error('Start Time must be set before selecting Complete Time');
            return false;
        }

        // If SP Request Time is set and Complete Time is being set, SP Received Time must be set
        if (completeTime && spRequestTime && !spReceivedTime) {
            this.toaster.error('SP Received Time must be set before completing when SP Request Time exists');
            return false;
        }

        // If both times are set, start time must be less than complete time
        if (startTime && completeTime) {
            const startDate = new Date(startTime);
            const completeDate = new Date(completeTime);

            if (startDate >= completeDate) {
                this.toaster.error('Start Time must be less than Complete Time');
                return false;
            }
        }

        // Complete time should not be greater than current time
        if (completeTime) {
            const completeDate = new Date(completeTime);
            if (completeDate > currentTime) {
                this.toaster.error('Complete Time cannot be greater than current time');
                return false;
            }
        }

        // Validate SP Request Time
        if (spRequestTime && !startTime) {
            this.toaster.error('Start Time must be set before SP Request Time');
            return false;
        }

        if (startTime && spRequestTime) {
            const startDate = new Date(startTime);
            const spRequestDate = new Date(spRequestTime);

            if (spRequestDate <= startDate) {
                this.toaster.error('SP Request Time must be greater than Start Time');
                return false;
            }
        }

        // Validate SP Received Time
        if (spReceivedTime && !spRequestTime) {
            this.toaster.error('SP Request Time must be set before SP Received Time');
            return false;
        }

        if (spRequestTime && spReceivedTime) {
            const spRequestDate = new Date(spRequestTime);
            const spReceivedDate = new Date(spReceivedTime);

            if (spReceivedDate <= spRequestDate) {
                this.toaster.error('SP Received Time must be greater than SP Request Time');
                return false;
            }
        }

        return true;
    }

    setupTimeValidationListeners(): void {
        // Listen to changes in subTaskCompleteTime
        this.subTaskForm.get('subTaskCompleteTime')?.valueChanges.subscribe(completeTime => {
            if (completeTime) {
                const startTime = this.subTaskForm.get('subTaskStartTime')?.value;
                const currentTime = new Date();
                const completeDate = new Date(completeTime);

                // Check if start time is set
                if (!startTime) {
                    this.subTaskForm.get('subTaskCompleteTime')?.setValue(null, { emitEvent: false });
                    this.toaster.error('Start Time must be set before selecting Complete Time');
                    return;
                }

                // Check if complete time is in the future
                if (completeDate > currentTime) {
                    this.subTaskForm.get('subTaskCompleteTime')?.setValue(null, { emitEvent: false });
                    this.toaster.error('Complete Time cannot be greater than current time');
                    return;
                }

                // Check if start time is less than complete time
                const startDate = new Date(startTime);
                if (startDate >= completeDate) {
                    this.subTaskForm.get('subTaskCompleteTime')?.setValue(null, { emitEvent: false });
                    this.toaster.error('Start Time must be less than Complete Time');
                    return;
                }
            }
        });

        // Listen to changes in subTaskStartTime to enable/disable complete time
        this.subTaskForm.get('subTaskStartTime')?.valueChanges.subscribe(startTime => {
            const completeTime = this.subTaskForm.get('subTaskCompleteTime')?.value;

            // If start time is cleared and complete time is set, clear complete time
            if (!startTime && completeTime) {
                this.subTaskForm.get('subTaskCompleteTime')?.setValue(null, { emitEvent: false });
            }
        });

        // Listen to changes in subTaskSPRequestTime
        this.subTaskForm.get('subTaskSPRequestTime')?.valueChanges.subscribe(spRequestTime => {
            if (spRequestTime) {
                const startTime = this.subTaskForm.get('subTaskStartTime')?.value;

                // Check if start time is set
                if (!startTime) {
                    this.subTaskForm.get('subTaskSPRequestTime')?.setValue(null, { emitEvent: false });
                    this.toaster.error('Start Time must be set before selecting SP Request Time');
                    return;
                }

                // Check if SP Request time is greater than start time
                const startDate = new Date(startTime);
                const spRequestDate = new Date(spRequestTime);
                if (spRequestDate <= startDate) {
                    this.subTaskForm.get('subTaskSPRequestTime')?.setValue(null, { emitEvent: false });
                    this.toaster.error('SP Request Time must be greater than Start Time');
                    return;
                }
            }
        });

        // Listen to changes in subTaskSPReceivedTime
        this.subTaskForm.get('subTaskSPReceivedTime')?.valueChanges.subscribe(spReceivedTime => {
            if (spReceivedTime) {
                const spRequestTime = this.subTaskForm.get('subTaskSPRequestTime')?.value;

                // Check if SP Request time is set
                if (!spRequestTime) {
                    this.subTaskForm.get('subTaskSPReceivedTime')?.setValue(null, { emitEvent: false });
                    this.toaster.error('SP Request Time must be set before selecting SP Received Time');
                    return;
                }

                // Check if SP Received time is greater than SP Request time
                const spRequestDate = new Date(spRequestTime);
                const spReceivedDate = new Date(spReceivedTime);
                if (spReceivedDate <= spRequestDate) {
                    this.subTaskForm.get('subTaskSPReceivedTime')?.setValue(null, { emitEvent: false });
                    this.toaster.error('SP Received Time must be greater than SP Request Time');
                    return;
                }
            }
        });
    }

    updateSubTaskForm(): void {
        // Prevent form submission in view mode
        if (this.isViewMode) {
            return;
        }

        if (this.subTaskForm.invalid) {
            console.warn('Form is invalid');
            this.toaster.warning('Please fill all required fields');
            return;
        }

        // Validate time constraints
        if (!this.validateTimeConstraints()) {
            return;
        }

        this.spinner.show();
        const formValue = this.subTaskForm.getRawValue();

        // Determine status based on timestamps
        let status = 'Pending';
        if (formValue.subTaskCompleteTime) {
            status = 'Closed';
        } else if (formValue.subTaskStartTime) {
            status = 'InProcess';
        }

        // Create FormData for file upload
        const formData = new FormData();

        // Append file if any
        if (this.selectedFile) {
            formData.append('file', this.selectedFile);
        }

        // Build the payload object
        const payload = {
            Id: formValue.Id,
            TechnicianExecutionWOID: this.data.technicianExecutionWOID,
            WOId: this.data.WOId,
            Detail: formValue.woDetails,
            ResponsibleID: formValue.responsible,
            ExternalVendorId: formValue.externalVendor,
            TargetDate: formValue.targetDate ? new Date(formValue.targetDate).toISOString() : null,
            CompletionDate: formValue.subTaskCompleteTime ? new Date(formValue.subTaskCompleteTime).toISOString() : null,
            Status: status,
            RMPM: 'RM',
            PlantCode: this.plantCode,
            GroupCode: this.groupCode,
            UserCode: this.userCode,
            IsManager: false,
            SubTaskIsMachineBreakdown: formValue.subTaskIsMachineBreakdown === 1 ? true : formValue.subTaskIsMachineBreakdown === 0 ? false : null,
            SubTaskIsSpareParts: formValue.subTaskIsSpareParts === 1 ? true : formValue.subTaskIsSpareParts === 0 ? false : null,
            SubTaskSparePartCode: formValue.subTaskSparePartCode || null,
            SubTaskStartTime: formValue.subTaskStartTime ? new Date(formValue.subTaskStartTime).toISOString() : null,
            SubTaskSPRequestTime: formValue.subTaskSPRequestTime ? new Date(formValue.subTaskSPRequestTime).toISOString() : null,
            SubTaskSPReceivedTime: formValue.subTaskSPReceivedTime ? new Date(formValue.subTaskSPReceivedTime).toISOString() : null,
        };

        // Append payload as JSON string
        formData.append('values', JSON.stringify(payload));

        console.log('Updating payload:', payload);

        this.prevTaskService.updateSubTaskWorkOrderWithFiles(formData).subscribe({
            next: (response: string) => {
                this.spinner.hide();
                console.log('Subtask updated successfully:', response);
                this.toaster.success('SubTask Work Order Updated Successfully');

                // Clear file
                this.selectedFile = null;
                this.filePreview = null;
                this.refreshData();
                if (this.editDialogRef) {
                    this.editDialogRef.close();
                }
                this.cdr.detectChanges();
            },
            error: (err) => {
                console.error('Error updating subtask:', err);
                this.spinner.hide();
                this.toaster.error('Failed to update subtask');
                this.cdr.detectChanges();
            },
        });
    }

    refreshData(): void {
        if (!this.data?.technicianExecutionWOID && !this.data?.WOId) {
            // Check if rowData exists and has data
            if (this.data?.rowData && Array.isArray(this.data.rowData) && this.data.rowData.length > 0) {
                this.data = this.data.rowData[0];
            }
        }
        var technicianExecutionWOID = this.data?.technicianExecutionWOID || this.data?.TechnicianExecutionWOID;
        var WOId = this.data?.WOId || this.data?.WOID;
        // Refresh the grid data from parent or service
        if (technicianExecutionWOID && WOId) {
            this.prevTaskService
                .getSubTaskWorkOrderByTaskId(0, technicianExecutionWOID, WOId)
                .subscribe({
                    next: (res: any[]) => {
                        this.rowData = res.map((item) => ({
                            id: item.Id,
                            woDetails: item.Detail,
                            responsible: this.getResponsibleName(item.ResponsibleID) || item.ResponsibleName || item.ResponsibleID,
                            responsibleId: item.ResponsibleID,
                            externalVendor: item.ExternalVendor || null,
                            externalVendorId: item.ExternalVendorId,
                            status: item.Status,
                            targetDate: item.TargetDate ? item.TargetDate : '',
                            completionDate: item.CompletionDate ? item.CompletionDate : '',
                            CreatedBy: item.CreatedBy,
                            UpdatedBy: item.UpdatedBy,
                            subTaskIsMachineBreakdown: item.SubTaskIsMachineBreakdown,
                            subTaskIsSpareParts: item.SubTaskIsSpareParts,
                            subTaskSparePartCode: item.SubTaskSparePartCode || '',
                            subTaskStartTime: item.SubTaskStartTime || null,
                            subTaskSPRequestTime: item.SubTaskSPRequestTime || null,
                            subTaskSPReceivedTime: item.SubTaskSPReceivedTime || null,
                            attachmentPath: item.AttachmentPath || null,
                            attachmentType: item.AttachmentType || null,
                            attachmentExtension: item.AttachmentExtension || null,
                            isApproved: item.IsApproved !== undefined && item.IsApproved !== null ? item.IsApproved : null,
                            approvalDate: item.ApprovalDate || null,
                            approvedBy: item.ApprovedBy || null,
                        }));
                        if (this.gridApi) {
                            this.gridApi.setRowData(this.rowData);
                            this.gridColumnApi.autoSizeAllColumns();
                        }
                        this.cdr.detectChanges();
                    },
                    error: (err) => {
                        console.error('Error refreshing subtasks:', err);
                    },
                });
        }
    }

    formatCellData(value: any, formatType: string): string {
        return this.userConfigService.formatField(value, formatType);
    }

    getLangWords(): void {
        this.pageId = this.baseApi.getCurrentMenuID();
        const userMenus = this.baseApi.getUserPermissionsSession();
        this.moduleId = userMenus.find((x) => x.menuId === this.pageId)?.parentId;

        const key = {
            ModuleId: this.moduleId,
            PageId: this.pageId,
            LanguageId:
                this.translate.currentLang === 'en'
                    ? 1
                    : this.translate.currentLang === 'ar'
                        ? 2
                        : this.translate.currentLang === 'fr'
                            ? 3
                            : this.translate.currentLang === 'sp'
                                ? 4
                                : 1,
        };

        this.translater.getLangWords(key).subscribe((res: any) => {
            const obj: any = {};
            res.tagList.forEach((elem: any) => {
                obj[elem.tagName] = elem.translatedValue;
            });
            this.langObj = obj;
            if (this.gridApi) this.gridApi.refreshHeader();
        });
    }

    localizeHeader(params: any): string {
        const field = params.colDef.field;
        return this.langObj?.[field] || field;
    }

    getResponsibleName(responsibleId: string): string {
        if (!this.technicianList || !responsibleId) return '';
        const technician = this.technicianList.find(
            (tech: any) => tech.id === responsibleId || tech.employeeCode === responsibleId
        );
        return technician ? (technician.name || technician.fullName || technician.UserName) : '';
    }

    closeDialog(): void {
        this.dialogRef.close(true);
    }

    closeEditDialog(): void {
        if (this.editDialogRef) {
            this.editDialogRef.close();
        }
    }

    openFile(): void {
        this.fileInput.nativeElement.click();
    }

    readFile(event: any): void {
        const file = event.target.files[0];

        if (file) {
            // Validate file size
            if (file.size > this.maxFileSize) {
                this.toaster.warning(`File ${file.name} exceeds maximum size of 5MB`);
                event.target.value = null;
                return;
            }

            const fileName = file.name;
            const fileExtension = fileName.substring(fileName.lastIndexOf('.') + 1).toLowerCase();

            // Validate file types
            const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'png', 'jpg', 'jpeg', 'gif', 'bmp', 'tiff', 'webp'];
            if (!allowedExtensions.includes(fileExtension)) {
                this.toaster.warning(`File type .${fileExtension} is not allowed`);
                event.target.value = null;
                return;
            }

            this.selectedFile = file;

            // Determine file type
            let fileType = '';
            if (file.type.match('image.*')) {
                fileType = 'image';
            } else if (file.type.match('application.*')) {
                fileType = 'application';
            }

            let reader = new FileReader();
            reader.onload = (e: any) => {
                this.filePreview = {
                    FileType: fileType,
                    ImageSrc: e.target.result,
                    FileExtension: fileExtension,
                    FileName: fileName,
                };
            };
            reader.readAsDataURL(file);
        }
        // Reset file input
        event.target.value = null;
    }

    removeFile(): void {
        this.selectedFile = null;
        this.filePreview = null;
        if (this.fileInput && this.fileInput.nativeElement) {
            this.fileInput.nativeElement.value = null;
        }
    }

    getFileTypeFromExtension(extension: string): string {
        const imageExtensions = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'tiff', 'webp'];
        return imageExtensions.includes(extension.toLowerCase()) ? 'image' : 'document';
    }

    openApprovalConfirmation(data: any): void {
        Swal.fire({
            title: 'Subtask Approval',
            html: `<p>Do you want to approve or disapprove this subtask?</p><p><strong>WO Details:</strong> ${data.woDetails}</p>`,
            icon: 'question',
            showCancelButton: true,
            showDenyButton: true,
            confirmButtonText: '<i class="fa fa-check"></i> Approve',
            denyButtonText: '<i class="fa fa-times"></i> Disapprove',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#28a745',
            denyButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
        }).then((result) => {
            if (result.isConfirmed) {
                this.approveSubTask(data.id, true);
            } else if (result.isDenied) {
                this.approveSubTask(data.id, false);
            }
        });
    }

    approveSubTask(subTaskId: number, isApproved: boolean): void {
        this.spinner.show();

        const payload = {
            Id: subTaskId,
            IsApproved: isApproved,
            ApprovedBy: this.userCode,
            ApprovalRemarks: isApproved ? 'Approved by Manager' : 'Disapproved by Manager',
        };

        this.prevTaskService.approveSubTaskWorkOrder(payload).subscribe({
            next: (response: string) => {
                this.spinner.hide();
                console.log('Subtask approval updated:', response);
                this.toaster.success(
                    isApproved ? 'SubTask Approved Successfully' : 'SubTask Disapproved Successfully'
                );
                this.refreshData();
                this.cdr.detectChanges();
            },
            error: (err) => {
                console.error('Error updating subtask approval:', err);
                this.spinner.hide();
                this.toaster.error('Failed to update subtask approval');
                this.cdr.detectChanges();
            },
        });
    }
}